// IPv4/IPv6 arithmetic. Pure functions, no network — every tool built on this
// runs entirely in the browser, so results are instant and nothing about the
// addresses a user types is ever sent to a server.
//
// IPv6 uses BigInt throughout: a v6 address is 128 bits and JS numbers lose
// precision past 2^53, so anything doing address math with Number is wrong for
// most of the address space.

// ----------------------------------------------------------------- IPv4

export function parseIpv4(s: string): number | null {
  const m = s.trim().match(/^(\d{1,3})\.(\d{1,3})\.(\d{1,3})\.(\d{1,3})$/);
  if (!m) return null;
  const parts = m.slice(1, 5).map(Number);
  // Reject "01.2.3.4" style and out-of-range octets.
  if (parts.some((n) => n > 255)) return null;
  if (m.slice(1, 5).some((p) => p.length > 1 && p[0] === "0")) return null;
  return ((parts[0] << 24) | (parts[1] << 16) | (parts[2] << 8) | parts[3]) >>> 0;
}

export function formatIpv4(n: number): string {
  return [(n >>> 24) & 255, (n >>> 16) & 255, (n >>> 8) & 255, n & 255].join(".");
}

export type SubnetInfo = {
  cidr: string;
  netmask: string;
  wildcard: string;
  network: string;
  broadcast: string;
  firstHost: string;
  lastHost: string;
  totalAddresses: string;
  usableHosts: string;
  prefix: number;
  /** RFC 1918 / loopback / link-local etc. */
  scope: string;
  binaryMask: string;
};

export function subnetInfo(ipStr: string, prefix: number): SubnetInfo | null {
  const ip = parseIpv4(ipStr);
  if (ip === null || prefix < 0 || prefix > 32) return null;

  const mask = prefix === 0 ? 0 : (0xffffffff << (32 - prefix)) >>> 0;
  const network = (ip & mask) >>> 0;
  const broadcast = (network | (~mask >>> 0)) >>> 0;
  const total = 2 ** (32 - prefix);

  // /31 is a point-to-point link with no network/broadcast reserved (RFC 3021)
  // and /32 is a single host. The usual "total - 2" is wrong for both.
  let usable: number;
  let firstHost: number;
  let lastHost: number;
  if (prefix === 32) {
    usable = 1;
    firstHost = lastHost = network;
  } else if (prefix === 31) {
    usable = 2;
    firstHost = network;
    lastHost = broadcast;
  } else {
    usable = total - 2;
    firstHost = (network + 1) >>> 0;
    lastHost = (broadcast - 1) >>> 0;
  }

  return {
    cidr: `${formatIpv4(network)}/${prefix}`,
    netmask: formatIpv4(mask),
    wildcard: formatIpv4(~mask >>> 0),
    network: formatIpv4(network),
    broadcast: prefix >= 31 ? "—" : formatIpv4(broadcast),
    firstHost: formatIpv4(firstHost),
    lastHost: formatIpv4(lastHost),
    totalAddresses: total.toLocaleString("en-US"),
    usableHosts: usable.toLocaleString("en-US"),
    prefix,
    scope: ipv4Scope(network),
    binaryMask: formatIpv4(mask)
      .split(".")
      .map((o) => Number(o).toString(2).padStart(8, "0"))
      .join("."),
  };
}

function ipv4Scope(n: number): string {
  const o = [(n >>> 24) & 255, (n >>> 16) & 255, (n >>> 8) & 255, n & 255];
  if (o[0] === 10) return "Private (RFC 1918)";
  if (o[0] === 172 && o[1] >= 16 && o[1] <= 31) return "Private (RFC 1918)";
  if (o[0] === 192 && o[1] === 168) return "Private (RFC 1918)";
  if (o[0] === 127) return "Loopback";
  if (o[0] === 169 && o[1] === 254) return "Link-local (APIPA)";
  if (o[0] === 100 && o[1] >= 64 && o[1] <= 127) return "Carrier-grade NAT (RFC 6598)";
  if (o[0] >= 224 && o[0] <= 239) return "Multicast";
  if (o[0] >= 240) return "Reserved";
  if (o[0] === 0) return "This network";
  return "Public";
}

/** Dotted quad -> decimal, hex, and binary. */
export function ipv4Conversions(ipStr: string): { decimal: string; hex: string; binary: string } | null {
  const n = parseIpv4(ipStr);
  if (n === null) return null;
  return {
    decimal: String(n),
    hex: "0x" + n.toString(16).padStart(8, "0"),
    binary: formatIpv4(n)
      .split(".")
      .map((o) => Number(o).toString(2).padStart(8, "0"))
      .join("."),
  };
}

// ----------------------------------------------------------------- IPv6

/** Parse to a 128-bit BigInt, handling "::" elision and IPv4-suffix form. */
export function parseIpv6(s: string): bigint | null {
  let str = s.trim().toLowerCase();
  if (!str) return null;
  str = str.replace(/^\[|\]$/g, "").replace(/%.*$/, ""); // brackets, zone id

  // Trailing dotted-quad form: ::ffff:192.0.2.1
  const v4m = str.match(/(.*:)(\d{1,3}\.\d{1,3}\.\d{1,3}\.\d{1,3})$/);
  if (v4m) {
    const v4 = parseIpv4(v4m[2]);
    if (v4 === null) return null;
    str = v4m[1] + ((v4 >>> 16) & 0xffff).toString(16) + ":" + (v4 & 0xffff).toString(16);
  }

  const halves = str.split("::");
  if (halves.length > 2) return null;

  const toGroups = (part: string): string[] | null => {
    if (part === "") return [];
    const g = part.split(":");
    if (g.some((x) => x === "" || x.length > 4 || !/^[0-9a-f]+$/.test(x))) return null;
    return g;
  };

  let groups: string[];
  if (halves.length === 2) {
    const head = toGroups(halves[0]);
    const tail = toGroups(halves[1]);
    if (!head || !tail) return null;
    const fill = 8 - head.length - tail.length;
    if (fill < 1) return null; // "::" must stand for at least one group
    groups = [...head, ...Array(fill).fill("0"), ...tail];
  } else {
    const g = toGroups(str);
    if (!g || g.length !== 8) return null;
    groups = g;
  }

  let v = 0n;
  for (const g of groups) v = (v << 16n) | BigInt(parseInt(g, 16));
  return v;
}

/** All 8 groups, zero-padded — the unambiguous long form. */
export function expandIpv6(v: bigint): string {
  const groups: string[] = [];
  for (let i = 7; i >= 0; i--) {
    groups.push((((v >> BigInt(i * 16)) & 0xffffn).toString(16)).padStart(4, "0"));
  }
  return groups.join(":");
}

/** RFC 5952 canonical short form: lowercase, longest run of zeros elided once. */
export function compressIpv6(v: bigint): string {
  const groups: number[] = [];
  for (let i = 7; i >= 0; i--) groups.push(Number((v >> BigInt(i * 16)) & 0xffffn));

  // Find the longest run of consecutive zero groups (length >= 2 to be worth
  // eliding; RFC 5952 forbids shortening a single zero group to "::").
  let bestStart = -1;
  let bestLen = 0;
  let curStart = -1;
  let curLen = 0;
  for (let i = 0; i < 8; i++) {
    if (groups[i] === 0) {
      if (curStart < 0) curStart = i;
      curLen++;
      if (curLen > bestLen) {
        bestLen = curLen;
        bestStart = curStart;
      }
    } else {
      curStart = -1;
      curLen = 0;
    }
  }

  const hex = groups.map((g) => g.toString(16));
  if (bestLen < 2) return hex.join(":");
  const head = hex.slice(0, bestStart).join(":");
  const tail = hex.slice(bestStart + bestLen).join(":");
  return `${head}::${tail}`;
}

export type V6Range = {
  network: string;
  networkExpanded: string;
  first: string;
  last: string;
  prefix: number;
  totalAddresses: string;
  scope: string;
};

export function ipv6Range(addr: string, prefix: number): V6Range | null {
  const v = parseIpv6(addr);
  if (v === null || prefix < 0 || prefix > 128) return null;
  const hostBits = BigInt(128 - prefix);
  const mask = hostBits === 128n ? 0n : (~0n << hostBits) & ((1n << 128n) - 1n);
  const network = v & mask;
  const last = network | ((1n << hostBits) - 1n);
  const total = 1n << hostBits;
  return {
    network: compressIpv6(network),
    networkExpanded: expandIpv6(network),
    first: compressIpv6(network),
    last: compressIpv6(last),
    prefix,
    totalAddresses: total > 10n ** 15n ? `2^${128 - prefix}` : total.toLocaleString("en-US"),
    scope: ipv6Scope(network),
  };
}

function ipv6Scope(v: bigint): string {
  const top = v >> 112n;
  if (v === 1n) return "Loopback (::1)";
  if (v === 0n) return "Unspecified (::)";
  if ((top & 0xfe00n) === 0xfc00n) return "Unique local (RFC 4193)";
  if ((top & 0xffc0n) === 0xfe80n) return "Link-local";
  if ((top & 0xff00n) === 0xff00n) return "Multicast";
  // ::ffff:a.b.c.d — the ffff group sits at bits 32-47, so the whole top 96
  // bits equal 0xffff. Shifting by 96 (the tempting arithmetic) yields 0.
  if ((v >> 32n) === 0xffffn) return "IPv4-mapped";
  if (top === 0x2002n) return "6to4";
  if ((top & 0xe000n) === 0x2000n) return "Global unicast";
  return "Reserved / special";
}

/** Smallest prefix that covers first..last, or null when it isn't a clean CIDR. */
export function ipv6RangeToCidr(firstStr: string, lastStr: string): string | null {
  const a = parseIpv6(firstStr);
  const b = parseIpv6(lastStr);
  if (a === null || b === null || b < a) return null;
  let prefix = 128;
  for (let p = 128; p >= 0; p--) {
    const hostBits = BigInt(128 - p);
    const mask = hostBits === 128n ? 0n : (~0n << hostBits) & ((1n << 128n) - 1n);
    if ((a & mask) === (b & mask)) {
      prefix = p;
      break;
    }
  }
  const hostBits = BigInt(128 - prefix);
  const mask = hostBits === 128n ? 0n : (~0n << hostBits) & ((1n << 128n) - 1n);
  const net = a & mask;
  const end = net | ((1n << hostBits) - 1n);
  // Only exact when the supplied bounds are the whole block.
  if (net !== a || end !== b) return null;
  return `${compressIpv6(net)}/${prefix}`;
}

/**
 * IPv4 -> IPv6 in the two forms people actually mean.
 * 6to4 is deprecated (RFC 7526) and labelled as such rather than omitted,
 * because it still turns up in old configs people are trying to decode.
 */
export function ipv4ToIpv6(ipStr: string): { mapped: string; sixToFour: string; compatible: string } | null {
  const n = parseIpv4(ipStr);
  if (n === null) return null;
  const hi = ((n >>> 16) & 0xffff).toString(16);
  const lo = (n & 0xffff).toString(16);
  return {
    mapped: `::ffff:${hi}:${lo}`,
    sixToFour: `2002:${hi}:${lo}::/48`,
    compatible: `::${hi}:${lo}`,
  };
}

/** Extract the embedded IPv4 from mapped/compatible/6to4 forms, if present. */
export function ipv6ToIpv4(addr: string): { ipv4: string; form: string } | null {
  const v = parseIpv6(addr);
  if (v === null) return null;
  if (v >> 32n === 0xffffn) return { ipv4: formatIpv4(Number(v & 0xffffffffn)), form: "IPv4-mapped (::ffff:a.b.c.d)" };
  if (v >> 112n === 0x2002n) return { ipv4: formatIpv4(Number((v >> 80n) & 0xffffffffn)), form: "6to4 (2002::/16)" };
  if (v >> 32n === 0n && v !== 0n && v !== 1n) return { ipv4: formatIpv4(Number(v & 0xffffffffn)), form: "IPv4-compatible (deprecated)" };
  return null;
}

/** RFC 4193 unique-local generator: fd00::/8 + 40 random bits. */
export function generateUla(): { prefix: string; globalId: string; example: string } {
  const bytes = new Uint8Array(5);
  crypto.getRandomValues(bytes);
  const globalId = Array.from(bytes, (b) => b.toString(16).padStart(2, "0")).join("");
  const g = globalId;
  const prefix = `fd${g.slice(0, 2)}:${g.slice(2, 6)}:${g.slice(6, 10)}::/48`;
  return {
    prefix,
    globalId: g,
    example: `fd${g.slice(0, 2)}:${g.slice(2, 6)}:${g.slice(6, 10)}:1::1`,
  };
}
